import { create } from "zustand";

/**
 * An open tab, identified by the document's **fileId** — its stable
 * lowercase-slug identity and its storage path segment (see src/data/fs.ts and
 * .claude/yaqatw-backend-reference.md). Tabs are keyed by fileId, never by the
 * display `name`: names are free-form (mixed case, spaces) and the cloud
 * lowercases path segments on write, so keying storage/addressing by `name`
 * made a document's folder diverge across devices. `name` here is a display
 * label only and may lag the on-disk value until the doc is reloaded.
 */
export interface OpenTab {
  id: string;
  name: string;
}

interface TabsStore {
  openTabs: OpenTab[];
  activeId: string | null;

  setTabs: (openTabs: OpenTab[], activeId: string | null) => void;
  /** Add the tab (or refresh its display name) and make it active. */
  openTab: (id: string, name: string) => void;
  closeTab: (id: string) => void;
  /** Replace one tab's identity in place — used by Save As, which mints a new
   *  fileId for the current tab rather than opening a second one. */
  replaceTab: (oldId: string, id: string, name: string) => void;
  clear: () => void;
}

export const useTabsStore = create<TabsStore>((set) => ({
  openTabs: [],
  activeId: null,

  setTabs: (openTabs, activeId) => set({ openTabs, activeId }),

  openTab: (id, name) =>
    set((state) => ({
      openTabs: state.openTabs.some((t) => t.id === id)
        ? state.openTabs.map((t) => (t.id === id ? { id, name } : t))
        : [...state.openTabs, { id, name }],
      activeId: id,
    })),

  closeTab: (id) =>
    set((state) => ({ openTabs: state.openTabs.filter((t) => t.id !== id) })),

  replaceTab: (oldId, id, name) =>
    set((state) => ({
      openTabs: state.openTabs.map((t) => (t.id === oldId ? { id, name } : t)),
      activeId: state.activeId === oldId ? id : state.activeId,
    })),

  clear: () => set({ openTabs: [], activeId: null }),
}));
